import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import SettingsHeading from '@/shared-components/text/components/SettingsHeading';
import Text from '@/shared-components/text/components/Text';

import './index.css';

function startModeTransitionCircleBlur(updateFn) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    updateFn();
    return;
  }

  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    updateFn();
    return;
  }

  const supportsVT = 'startViewTransition' in document;
  const styleId = `mode-transition-${Date.now()}`;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @supports (view-transition-name: root) {
      ::view-transition-old(root) { animation: none; }
      ::view-transition-new(root) {
        animation: circle-blur-expand 500ms ease-out;
        transform-origin: top right;
      }
      @keyframes circle-blur-expand {
        from { clip-path: circle(0% at 100% 0%); filter: blur(4px); }
        to   { clip-path: circle(150% at 100% 0%); filter: blur(0); }
      }
    }
  `;
  document.head.appendChild(style);

  const cleanup = () => {
    const el = document.getElementById(styleId);
    if (el) el.remove();
  };

  const runUpdate = () => {
    try {
      updateFn();
    } finally {
      setTimeout(cleanup, 800);
    }
  };

  if (supportsVT) {
    // @ts-ignore
    document.startViewTransition(runUpdate);
  } else {
    runUpdate();
  }
}

export function ModeAndThemeOption({ className="", text="", isActive=false, onClick=null }) {
  return (
    <>
      <div className={`settings-mode-theme__option${isActive ? " active" : ""} ${className} element-non-selectable`} onClick={onClick}>
        <div className="settings-mode-theme__option-circle"/>

        <Text className="settings-mode-theme__option-text font-size-medium">
          {text}
        </Text>
      </div>
    </>
  );
}

export default function ModeAndTheme() {
  const { state, stateHandler } = useStateHandler();
  const t = useTranslations('common.user_settings.mode_theme');

  function updateMode(newMode) {
    const currentMode = state.user_session.user_preferences.mode;
    if (currentMode === newMode) return;

    startModeTransitionCircleBlur(() => {
      stateHandler.set("user_session.user_preferences.mode", newMode);
      UserService.updateUserPreferences({ "user_preferences.mode": newMode });
    });
  }

  function updateTheme(newTheme) {
    stateHandler.set("user_session.user_preferences.theme", newTheme);
    UserService.updateUserPreferences({ "user_preferences.theme": newTheme });
  }

  return (
    <>
      <SettingsHeading text={t('heading')}/>
      <div className="settings-mode-theme__container">
        <Text className="settings-mode-theme__heading font-size-large">
          {t('sections.mode')}
        </Text>

        <ModeAndThemeOption 
          className="dark-mode mode" 
          text="Dark" 
          isActive={state.user_session.user_preferences.mode === "dark"} 
          onClick={()=>updateMode("dark")}
        />

        <ModeAndThemeOption 
          className="light-mode mode" 
          text="Light" 
          isActive={state.user_session.user_preferences.mode === "light"} 
          onClick={()=>updateMode("light")}
        />

        <Text className="settings-mode-theme__heading font-size-large">
          {t('sections.theme')}
        </Text>

        <ModeAndThemeOption 
          className="starry" 
          text="Starry moon" 
          isActive={state.user_session.user_preferences.theme === "starry_moon"} 
          onClick={()=>updateTheme("starry_moon")}
        />

        <ModeAndThemeOption 
          className="dancing" 
          text="Dancing on the grass" 
          isActive={state.user_session.user_preferences.theme === "dancing-on-the-grass"} 
          onClick={()=>updateTheme("dancing-on-the-grass")}
        />

        <ModeAndThemeOption 
          className="fountain" 
          text="Fountain of delights" 
          isActive={state.user_session.user_preferences.theme === "fountain-of-delights"} 
          onClick={()=>updateTheme("fountain-of-delights")}
        />

        <ModeAndThemeOption 
          className="water" 
          text="Swinging dress" 
          isActive={state.user_session.user_preferences.theme === "swinging-dress"} 
          onClick={()=>updateTheme("swinging-dress")}
        />

        <ModeAndThemeOption 
          className="violet" 
          text="Water lily pond" 
          isActive={state.user_session.user_preferences.theme === "water-lily-pond"} 
          onClick={()=>updateTheme("water-lily-pond")}
        />
      </div>
    </>
  );
}
